import { useState, useEffect } from 'react';
import { db, auth } from '../firebaseConfig'; // Importamos 'auth' para saber quién es el admin
import { collection, onSnapshot, query, orderBy, doc, getDoc } from 'firebase/firestore'; // Añadimos doc y getDoc
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

// La estructura de datos se queda igual
interface ChartData {
  date: string;
  [userId: string]: number | string;
}

// La paleta de colores se queda igual
const colorPalette = ['#f472b6', '#82ca9d', '#ffc658', '#a4de6c', '#8dd1e1', '#d0ed57'];

export const UsageChart = () => {
  const [data, setData] = useState<ChartData[]>([]);
  const [activeUsers, setActiveUsers] = useState<{ id: string; name: string; color: string }[]>([]);

  useEffect(() => {
    const q = query(collection(db, 'pageVisits'), orderBy('timestamp', 'asc'));
    // Hacemos la función del snapshot asíncrona para poder usar await dentro
    const unsubscribe = onSnapshot(q, async (querySnapshot) => {
      const adminId = auth.currentUser?.uid;
      
      const visitsByDay = new Map<string, { [userId: string]: number }>();
      const uniqueUserIds = new Set<string>();
      
      querySnapshot.forEach((doc) => {
        const visit = doc.data() as { timestamp: any; userId: string };
        if (visit.timestamp && visit.userId) {
          uniqueUserIds.add(visit.userId);
          const date = visit.timestamp.toDate();
          const dayKey = format(date, 'd MMM', { locale: es });

          const currentCounts = visitsByDay.get(dayKey) || {};
          currentCounts[visit.userId] = (currentCounts[visit.userId] || 0) + 1;
          
          visitsByDay.set(dayKey, currentCounts);
        }
      });

      // --- NUEVA LÓGICA PARA OBTENER NOMBRES REALES ---
      const userProfiles = new Map<string, string>();
      // Creamos una lista de promesas para buscar cada perfil de usuario en la colección 'users'
      const userPromises = Array.from(uniqueUserIds).map(uid => getDoc(doc(db, "users", uid)));
      const userDocs = await Promise.all(userPromises);

      userDocs.forEach(docSnap => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          // Usamos el displayName, si no existe, el email.
          const name = userData.displayName || userData.email || `Usuario Desconocido`;
          userProfiles.set(docSnap.id, name);
        }
      });
      // --- FIN DE LA NUEVA LÓGICA ---

      // Lógica para asignar nombres y colores, ahora usando los perfiles que encontramos
      const users = Array.from(uniqueUserIds).map((id, index) => {
        // Obtenemos el nombre del perfil, si no lo encontramos, usamos un genérico
        const name = userProfiles.get(id) || `Usuario ${index + 1}`;
        
        if (id === adminId) {
          return { id, name: 'Tus Visitas', color: 'var(--color-primary)' };
        }
        return { id, name, color: colorPalette[index % colorPalette.length] };
      });
      setActiveUsers(users);
      
      // El resto de la lógica se queda igual
      const chartData: ChartData[] = Array.from(visitsByDay, ([date, userCounts]) => ({ 
        date, 
        ...userCounts
      }));
      setData(chartData);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="bg-white/50 p-4 rounded-xl shadow-lg h-80">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
          <XAxis dataKey="date" stroke="#4c4a7e" fontSize={12} />
          <YAxis allowDecimals={false} stroke="#4c4a7e" fontSize={12} />
          <Tooltip
            cursor={{ fill: 'rgba(124, 58, 237, 0.1)' }}
            contentStyle={{
              backgroundColor: 'rgba(255,255,255,0.8)',
              border: '1px solid #ddd',
              borderRadius: '0.5rem',
            }}
          />
          <Legend wrapperStyle={{ fontSize: '14px' }} />
          
          {/* El renderizado dinámico de las barras se queda igual */}
          {activeUsers.map(user => (
            <Bar 
              key={user.id} 
              dataKey={user.id} 
              stackId="a" 
              name={user.name} 
              fill={user.color} 
              barSize={30} 
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
